import React from 'react';

import { Select, Switch, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';

import { ATTR_META } from '@pages/Employees/data/constants';
import { useSourceOptions } from '@pages/Employees/hooks';
import { AttrSource, IncludeExclude, Mode, RuleValue } from '@pages/Employees/interfaces';

import { AttrLabel, AttrRow, ChipsRow } from './styles';
interface IProps {
  attribute: string;
  source: Exclude<AttrSource, AttrSource.Geo>;
  lists: IncludeExclude;
  onChange: (lists: IncludeExclude) => void;
}
/** Универсальный контрол атрибута: переключатель Включить/Исключить + селект + чипсы. */
export const AttributeControl: React.FC<IProps> = ({ attribute, source, lists, onChange }) => {
  const { t } = useTranslation(NSKeys.assignments);
  const [mode, setMode] = React.useState<Mode>('include');
  const [selected, setSelected] = React.useState<RuleValue | undefined>(undefined);
  const { options, isLoading, labelOf } = useSourceOptions(source);
  const add = (value: RuleValue) => {
    const current = lists[mode];
    if (!current.some((x) => String(x) === String(value))) {
      onChange({ ...lists, [mode]: [...current, value] });
    }
    setSelected(undefined);
  };
  const remove = (list: Mode, value: RuleValue) => {
    onChange({ ...lists, [list]: lists[list].filter((x) => String(x) !== String(value)) });
  };
  const hasChips = lists.include.length > 0 || lists.exclude.length > 0;
  return (
    <AttrRow>
      <AttrLabel>{t(ATTR_META[attribute as keyof typeof ATTR_META]?.labelKey as any)}</AttrLabel>
      <Switch
        checked={mode === 'include'}
        checkedChildren={t('toggle.include')}
        unCheckedChildren={t('toggle.exclude')}
        onChange={(checked) => setMode(checked ? 'include' : 'exclude')}
      />
      <Select
        style={{ minWidth: 220, flex: 1 }}
        showSearch
        allowClear
        loading={isLoading}
        value={selected as any}
        placeholder={t('select.placeholder')}
        options={options}
        optionFilterProp="label"
        onChange={(v) => (v === undefined || v === null ? setSelected(undefined) : add(v as RuleValue))}
        notFoundContent={isLoading ? undefined : t('select.empty')}
      />
      {hasChips && (
        <ChipsRow style={{ flexBasis: '100%' }}>
          {lists.include.length > 0 && (
            <>
              <Typography.Text type="secondary">{t('toggle.include')}:</Typography.Text>
              {lists.include.map((v) => (
                <Tag key={`i-${v}`} closable color="blue" onClose={() => remove('include', v)}>
                  {labelOf(v)}
                </Tag>
              ))}
            </>
          )}
          {lists.exclude.length > 0 && (
            <>
              <Typography.Text type="secondary">{t('toggle.exclude')}:</Typography.Text>
              {lists.exclude.map((v) => (
                <Tag key={`e-${v}`} closable color="red" onClose={() => remove('exclude', v)}>
                  {labelOf(v)}
                </Tag>
              ))}
            </>
          )}
        </ChipsRow>
      )}
    </AttrRow>
  );
};
